import React, { useCallback, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout as EmbeddedCheckoutBase,
} from "@stripe/react-stripe-js";
import { Typography } from "@mui/material";

import { stripeMicroService } from "@/shared/services/stripeMicroService";
import FullScreenLoader from "@/components/loaders/FullScreenLoader";
import { logger } from "@/helpers/loggerHelper";
import { ENV } from "@/config/appConfig";

const stripePromise = loadStripe(ENV.STRIPE_PUBLISHABLE_KEY);

type CheckoutPayload = Record<string, unknown>;

type ClientSecretResponse = {
  data?: {
    result?: {
      client_secret?: string;
    };
  };
};

type ClientSecretApi = (
  _data: CheckoutPayload
) => Promise<ClientSecretResponse>;

type EmbeddedCheckoutFormProps = {
  api?: ClientSecretApi;
  data: CheckoutPayload;
  onError?: (_error: unknown) => void;
};

const clientSecretRequestCache = new Map<string, Promise<string>>();

const EmbeddedCheckout = EmbeddedCheckoutBase as unknown as React.ComponentType;

function getClientSecretRequestKey(
  data: CheckoutPayload,
  api?: ClientSecretApi
) {
  return `${api ? "custom" : "subscription"}:${JSON.stringify(data)}`;
}

function requestClientSecret(data: CheckoutPayload, api?: ClientSecretApi) {
  const requestKey = getClientSecretRequestKey(data, api);
  const cachedRequest = clientSecretRequestCache.get(requestKey);

  if (cachedRequest) {
    return cachedRequest;
  }

  const request = (
    api
      ? api(data)
      : (stripeMicroService.subscriptionPayment(
          data
        ) as Promise<ClientSecretResponse>)
  )
    .then((res) => {
      if (!res?.data?.result?.client_secret) {
        throw new Error("Invalid response format");
      }

      return res.data.result.client_secret;
    })
    .finally(() => {
      clientSecretRequestCache.delete(requestKey);
    });

  clientSecretRequestCache.set(requestKey, request);

  return request;
}

const EmbeddedCheckoutForm = ({
  data,
  onError,
  api,
}: EmbeddedCheckoutFormProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;

    async function initializeCheckout() {
      try {
        const secret = await requestClientSecret(data, api);

        if (isActive) {
          setClientSecret(secret);
        }
      } catch (err) {
        if (!isActive) return;

        setError(err instanceof Error ? err.message : String(err));
        logger.error("Error fetching client secret:", err);
        if (onError && typeof onError === "function") {
          onError(err);
        }
      }
    }

    initializeCheckout();

    return () => {
      isActive = false;
    };
  }, [data, onError, api]);

  const handleOnComplete = useCallback(() => {}, []);

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center flex-column">
        <Typography
          variant="body1"
          color="error"
          className="fw-sb my-2"
          gutterBottom
        >
          Error initializing checkout
        </Typography>
        <Typography variant="body2" color="text.secondary" className="my-1">
          Please try again or contact support if the problem persists.
        </Typography>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <FullScreenLoader isActive={!clientSecret} message="Please wait..." />
    );
  }

  return (
    <div id="checkout" className="w-100">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          clientSecret,
          onComplete: handleOnComplete,
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default EmbeddedCheckoutForm;
